import React, {useRef, useState} from 'react';
import {useMutation, useQuery} from "@tanstack/react-query";
import {addMinutes, subDays} from "date-fns";
import {Template} from "../../../components/template/template.jsx";
import Button from "../../../components/button/button.jsx";
import PassageFilters from "./components/passage-filters.jsx";
import PassageResults from "./components/passage-results.jsx";
import PassageDetail from "./components/passage-detail.jsx";
import IndividualPassageSearchMap from "./components/individual-passage-search-map.jsx";
import IndividualPassageSearchService from "../../../services/web/individual-passage-search-service.js";
import projectUtils from "../../../utils/project-utils.js";
import {errMessageI18n} from "./utils/individual-passage-search-utils.js";
import "./individual-passage-search.css";

const getInitialFilters = () => ({
    dtStart: subDays(new Date(), 7),
    dtEnd: addMinutes(new Date(), 1),
    providers: [],
    cpf: "",
    image: undefined,
    onlyMatches: false,
});

const IndividualPassageSearch = () => {
    const [filters, setFilters] = useState(getInitialFilters);
    const [threshold, setThreshold] = useState(70);
    const [imgSrc, setImgSrc] = useState(undefined);
    const [events, setEvents] = useState(undefined);
    const [eventSelected, setEventSelected] = useState(undefined);
    const [target, setTarget] = useState("cpf");
    const [searchedImage, setSearchedImage] = useState(undefined);
    const [mapModal, setMapModal] = useState(false);

    const selectionSeq = useRef(0);

    const {data: providers} = useQuery({
        queryFn: () => IndividualPassageSearchService.providers(),
        queryKey: ["individual-passage-providers"]
    });

    const {data: facialRecognitionStatus} = useQuery({
        queryFn: () => IndividualPassageSearchService.facialRecognitionStatus(),
        queryKey: ["facial-recognition-status"]
    });

    const searchMutation = useMutation({
        mutationFn: (formData) => IndividualPassageSearchService.search(formData),
        onSuccess: (data) => setEvents(data),
        onError: (error) => {
            const message = error?.response?.data?.message;
            console.error(error);
            projectUtils.swalErrorMessage("", errMessageI18n(message ?? ""), "Ok");
            setEvents(undefined);
        }
    });

    const dateTimeValidate = (startVal, endVal, formData) => {
        if (!startVal) {
            projectUtils.swalErrorMessage("", 'O campo "Data/hora inicial" é obrigatório', "Ok");
            return false;
        }

        if (!endVal) {
            projectUtils.swalErrorMessage("", 'O campo "Data/hora final" é obrigatório', "Ok");
            return false;
        }

        if (startVal > endVal) {
            projectUtils.swalErrorMessage("", 'A data inicial não pode ser maior que a data final.', "Ok");
            return false;
        }

        const formatDate = (date) => new Date(date).toISOString().slice(0, 23) + 'Z';

        formData.append("dtIni", formatDate(startVal));
        formData.append("dtEnd", formatDate(endVal));

        return true;
    };

    const search = () => {
        const formData = new FormData();

        formData.append("providers", JSON.stringify(filters.providers?.map(item => ({id: item.value, name: item.label, type: item.type}))));
        formData.append("threshold", (threshold / 100).toString());
        formData.append("onlyMatches", filters.onlyMatches);

        if (!dateTimeValidate(filters.dtStart, filters.dtEnd, formData)) return;

        setEventSelected(undefined);
        setSearchedImage(undefined);
        setTarget("cpf");

        if (filters.image) {
            setTarget("image");
            setSearchedImage(imgSrc);
            formData.append("image", filters.image);
        }

        if (filters.cpf) formData.append("cpf", filters.cpf?.replaceAll(".", "").replace("-", ""));

        searchMutation.mutate(formData);
    };

    const clearList = () => {
        setFilters(getInitialFilters());
        setThreshold(70);
        setEvents(undefined);
        setEventSelected(undefined);
    };

    const handleEvent = async (event, index) => {
        if (eventSelected?.id === index) {
            setEventSelected(undefined);
            return;
        }

        const seq = ++selectionSeq.current;
        const snapshot = await IndividualPassageSearchService.snapshot(event.eventDateTime, event.cameraId);
        if (seq !== selectionSeq.current) return;

        setEventSelected({...event, id: index, snapshot});
    };

    return (
        <Template isLoading={searchMutation.isPending}>
            <Template.Header>
                <Template.Title>Pesquisa</Template.Title>

                {
                    events && events.length > 0 &&
                    <Button
                        target={"primary"}
                        disabled={!events?.filter(e => e.latitude && e.longitude).length}
                        onClick={() => setMapModal(true)}
                    >
                        Ver passagens no mapa
                    </Button>
                }
            </Template.Header>

            <Template.Body>
                <div className={"flex gap-2"}>
                    <PassageFilters
                        filters={filters}
                        setFilters={setFilters}
                        threshold={threshold}
                        setThreshold={setThreshold}
                        imgSrc={imgSrc}
                        setImgSrc={setImgSrc}
                        providers={providers}
                        facialRecognitionStatus={facialRecognitionStatus}
                        onSearch={search}
                        onClear={clearList}
                    />

                    <PassageResults
                        events={events}
                        selectedId={eventSelected?.id}
                        hasSelection={!!eventSelected}
                        onSelectEvent={handleEvent}
                    />

                    {
                        eventSelected &&
                        <PassageDetail
                            eventSelected={eventSelected}
                            target={target}
                            searchedImage={searchedImage}
                            setEventSelected={setEventSelected}
                        />
                    }
                </div>
            </Template.Body>

            <IndividualPassageSearchMap events={events} show={mapModal} setShow={setMapModal}/>
        </Template>
    );
};

export default IndividualPassageSearch;
